'use client'

import { motion } from 'framer-motion'
import { Check } from 'lucide-react'

import { cn } from '@/lib/utils'

// A single selectable answer inside the /pricing wizard (Task 3 of
// docs/superpowers/plans/2026-07-14-pricing-calculator.md). Renders purely
// from the config's option shape (key/label/description) — no hardcoded
// copy lives here, only structure and interaction.
//
// `icon` is a forward-compatible slot: the Calculator global has no
// per-option icon field yet, so by default this badge just doubles as the
// selection indicator (a filled check for single/multi). If a future
// config revision adds icons, passing `icon` here swaps the badge for that
// glyph without changing the card's layout or touch target.
export function OptionCard({
  option,
  selected = false,
  onSelect,
  variant = 'single',
  icon: Icon = null,
  shouldReduceMotion = false,
}) {
  return (
    <motion.button
      type="button"
      role={variant === 'multi' ? 'checkbox' : 'radio'}
      aria-checked={selected}
      onClick={() => onSelect?.(option.key)}
      whileTap={shouldReduceMotion ? undefined : { scale: 0.98 }}
      className={cn(
        'flex min-h-11 w-full items-start gap-3 rounded-2xl border px-4 py-3.5 text-left transition-colors duration-150',
        selected
          ? 'border-kingdom-gold bg-kingdom-gold/10'
          : 'border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.05]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 flex size-6 shrink-0 items-center justify-center border transition-colors duration-150',
          variant === 'multi' ? 'rounded-md' : 'rounded-full',
          selected
            ? 'border-kingdom-gold bg-kingdom-gold/15 text-kingdom-gold'
            : 'border-white/25 bg-transparent text-transparent',
        )}
      >
        {Icon ? (
          <Icon className="size-4" />
        ) : selected ? (
          <Check className="size-4" strokeWidth={3} />
        ) : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-base leading-snug font-medium text-white sm:text-sm">
          {option.label}
        </span>
        {option.description && (
          <span className="mt-1 block text-sm leading-relaxed text-white/55">
            {option.description}
          </span>
        )}
      </span>
    </motion.button>
  )
}
